import LogRocket from 'logrocket'
import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiFlexGroup, EuiFlexItem, EuiLoadingContent, EuiText, EuiTitle } from '@elastic/eui'

import { useGetMasterCampaignPlanQuery } from 'api/masterCampaignPlansApi'

import MasterCampaignPlanPage from './new/MasterCampaignPlanPage'
import { MasterCampaignPlanPageOld } from './old/MasterCampaignPlanPageOld'

interface MasterCampaignPlanPageParams {
  id: string
}

const BaseNotFound = 404
const MasterCampaignPlanNotFoundStatus = 1404
const MasterCampaignPlanInEditModeStatus = 1412
const MasterCampaignPlanInDspStatus = 1410

enum ErrorState {
  NotFound = 'NotFound',
  InEditMode = 'InEditMode',
  InDsp = 'InDsp'
}

const MasterCampaignPlanPageDynamic: FC = () => {
  const [errorState, setErrorState] = useState<ErrorState>()
  const { id } = useParams<MasterCampaignPlanPageParams>()

  const { data: masterCampaignPlan, isFetching: isMasterCampaignPlanFetching, error: error } = useGetMasterCampaignPlanQuery({ publicId: id ?? '' }, { skip: !id })

  useEffect(() => {
    if (!masterCampaignPlan) {
      return
    }

    LogRocket.info(`Campaign Preview plan loaded:\nAccount Name: ${masterCampaignPlan.accountName}\nPlan Name: ${masterCampaignPlan.name}`)
  }, [masterCampaignPlan])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (error?.originalStatus) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const errorStatus = error.originalStatus
      if (errorStatus === MasterCampaignPlanNotFoundStatus || errorStatus === BaseNotFound) {
        setErrorState(ErrorState.NotFound)
      } else if (errorStatus === MasterCampaignPlanInEditModeStatus) {
        setErrorState(ErrorState.InEditMode)
      } else if (errorStatus === MasterCampaignPlanInDspStatus) {
        setErrorState(ErrorState.InDsp)
      }
    } else {
      setErrorState(undefined)
    }
  }, [error])

  if (isMasterCampaignPlanFetching && !masterCampaignPlan && !error) {
    return <EuiLoadingContent />
  }

  const hasPersonas = masterCampaignPlan?.personas && masterCampaignPlan.personas.length > 0

  return (
    <>
      {hasPersonas ? <MasterCampaignPlanPage /> : <MasterCampaignPlanPageOld />}
      {errorState === ErrorState.NotFound && (
        <EuiFlexGroup
          style={{
            height: '100%'
          }}
          direction='column'
          alignItems='center'
          justifyContent='center'>
          <EuiFlexItem grow={false}>
            <EuiTitle size='l'>
              <h1>The plan you are attempting to view cannot be found.</h1>
            </EuiTitle>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
      {errorState === ErrorState.InEditMode && (
        <EuiFlexGroup
          style={{
            height: '100%'
          }}
          direction='column'
          alignItems='center'
          justifyContent='center'>
          <EuiFlexItem grow={false}>
            <EuiTitle size='l'>
              <h1>This plan is currently being edited.</h1>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size='m'>
              <p>The plan will become viewable again once the edits have been completed.</p>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
      {errorState === ErrorState.InDsp && (
        <EuiFlexGroup
          style={{
            height: '100%'
          }}
          direction='column'
          alignItems='center'
          justifyContent='center'>
          <EuiFlexItem grow={false}>
            <EuiTitle size='l'>
              <h1>This plan is no longer viewable.</h1>
            </EuiTitle>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
    </>
  )
}

export default MasterCampaignPlanPageDynamic
